import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Crown, Mic } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import VoiceUsageCard from '../components/VoiceUsageCard';

const Settings = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState(i18n.resolvedLanguage?.startsWith('en') ? 'en' : 'pl');

  const handleLanguageChange = (value) => {
    const next = value === 'en' ? 'en' : 'pl';
    setLanguage(next);
    i18n.changeLanguage(next);
  };
  const [responseStyle, setResponseStyle] = useState('konkretnie');
  const [defaultMode, setDefaultMode] = useState('general');
  const [saveHistory, setSaveHistory] = useState(true);
  // Voice mode preferences (stored locally for now; backend persistence can come in v2)
  const [voiceInput, setVoiceInput] = useState(() => localStorage.getItem('hussar_voice_input') !== '0');
  const [voiceOutput, setVoiceOutput] = useState(() => localStorage.getItem('hussar_voice_output') !== '0');
  const [voiceAutoplay, setVoiceAutoplay] = useState(() => localStorage.getItem('hussar_voice_autoplay') === '1');
  const [voiceLanguage, setVoiceLanguage] = useState(() => localStorage.getItem('hussar_voice_language') || 'auto');
  const [voiceSpeed, setVoiceSpeed] = useState(() => localStorage.getItem('hussar_voice_speed') || '1.0');

  const persistVoice = (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* noop */
    }
  };

  return (
    <div className="hussar-app">
      {/* Header */}
      <header className="hussar-app-header">
        <div className="hussar-app-header-content">
          <Button 
            variant="ghost" 
            className="hussar-btn-back"
            onClick={() => navigate('/app')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="hussar-logo-full" onClick={() => navigate('/')}>
            <img src="/hussarai-logo-full.png" alt="HussarAi" className="hussar-logo-full-img-small" />
          </div>
          <div></div>
        </div>
      </header>

      {/* Settings Content */}
      <div className="hussar-settings-page">
        <div className="hussar-settings-container">
          <h1 className="hussar-settings-title">{t('settings.title')}</h1>

          <div className="hussar-settings-sections">
            {/* Language Settings */}
            <Card className="hussar-settings-card">
              <div className="hussar-settings-header">
                <h3 className="hussar-settings-section-title">{t('settings.languageSection')}</h3>
              </div>
              <div className="hussar-settings-item">
                <Label htmlFor="language" className="hussar-label">{t('settings.languageLabel')}</Label>
                <Select value={language} onValueChange={handleLanguageChange}>
                  <SelectTrigger id="language" className="hussar-select" data-testid="settings-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pl" data-testid="lang-option-pl">Polski</SelectItem>
                    <SelectItem value="en" data-testid="lang-option-en">English</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Card>

            {/* Voice Settings */}
            <Card className="hussar-settings-card" data-testid="voice-settings-card">
              <div className="hussar-settings-header">
                <div className="hussar-settings-header-row">
                  <Mic className="w-5 h-5" style={{ color: 'var(--hussar-accent)' }} />
                  <h3 className="hussar-settings-section-title">{t('settings.voiceSection')}</h3>
                </div>
                <p className="hussar-settings-description">{t('settings.voiceDescription')}</p>
              </div>

              <VoiceUsageCard />

              <div className="hussar-settings-item hussar-settings-switch">
                <div>
                  <Label className="hussar-label">{t('settings.voiceInputLabel')}</Label>
                  <p className="hussar-settings-hint">{t('settings.voiceInputHint')}</p>
                </div>
                <Switch
                  checked={voiceInput}
                  onCheckedChange={(v) => { setVoiceInput(v); persistVoice('hussar_voice_input', v ? '1' : '0'); }}
                  data-testid="voice-input-switch"
                />
              </div>

              <div className="hussar-settings-item hussar-settings-switch">
                <div>
                  <Label className="hussar-label">{t('settings.voiceOutputLabel')}</Label>
                  <p className="hussar-settings-hint">{t('settings.voiceOutputHint')}</p>
                </div>
                <Switch
                  checked={voiceOutput}
                  onCheckedChange={(v) => { setVoiceOutput(v); persistVoice('hussar_voice_output', v ? '1' : '0'); }}
                  data-testid="voice-output-switch"
                />
              </div>

              <div className="hussar-settings-item hussar-settings-switch">
                <div>
                  <Label className="hussar-label">{t('settings.voiceAutoplayLabel')}</Label>
                  <p className="hussar-settings-hint">{t('settings.voiceAutoplayHint')}</p>
                </div>
                <Switch
                  checked={voiceAutoplay}
                  onCheckedChange={(v) => { setVoiceAutoplay(v); persistVoice('hussar_voice_autoplay', v ? '1' : '0'); }}
                  data-testid="voice-autoplay-switch"
                />
              </div>

              <div className="hussar-settings-item">
                <Label className="hussar-label">{t('settings.voiceLanguageLabel')}</Label>
                <Select
                  value={voiceLanguage}
                  onValueChange={(v) => { setVoiceLanguage(v); persistVoice('hussar_voice_language', v); }}
                >
                  <SelectTrigger className="hussar-select" data-testid="voice-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t('settings.voiceLanguageAuto')}</SelectItem>
                    <SelectItem value="pl">{t('settings.voiceLanguagePl')}</SelectItem>
                    <SelectItem value="en">{t('settings.voiceLanguageEn')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="hussar-settings-item">
                <Label className="hussar-label">{t('settings.voiceSpeedLabel')}</Label>
                <Select
                  value={voiceSpeed}
                  onValueChange={(v) => { setVoiceSpeed(v); persistVoice('hussar_voice_speed', v); }}
                >
                  <SelectTrigger className="hussar-select" data-testid="voice-speed-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0.9">0.9×</SelectItem>
                    <SelectItem value="1.0">1.0×</SelectItem>
                    <SelectItem value="1.1">1.1×</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Card>


            {/* Response Style Settings */}
            <Card className="hussar-settings-card">
              <div className="hussar-settings-header">
                <h3 className="hussar-settings-section-title">{t('settings.styleSection')}</h3>
                <p className="hussar-settings-description">{t('settings.styleDescription')}</p>
              </div>
              <div className="hussar-settings-item">
                <Label htmlFor="style" className="hussar-label">{t('settings.stylePick')}</Label>
                <Select value={responseStyle} onValueChange={setResponseStyle}>
                  <SelectTrigger id="style" className="hussar-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="konkretnie">{t('settings.styleShort')}</SelectItem>
                    <SelectItem value="profesjonalnie">{t('settings.styleProfessional')}</SelectItem>
                    <SelectItem value="strategicznie">{t('settings.styleStrategic')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Card>

            {/* Default Mode Settings */}
            <Card className="hussar-settings-card">
              <div className="hussar-settings-header">
                <h3 className="hussar-settings-section-title">{t('settings.modeSection')}</h3>
              </div>
              <div className="hussar-settings-item">
                <Label htmlFor="mode" className="hussar-label">{t('settings.modeStart')}</Label>
                <Select value={defaultMode} onValueChange={setDefaultMode}>
                  <SelectTrigger id="mode" className="hussar-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">{t('settings.modeGeneral')}</SelectItem>
                    <SelectItem value="praca">{t('settings.modePraca')}</SelectItem>
                    <SelectItem value="biznes">{t('settings.modeBiznes')}</SelectItem>
                    <SelectItem value="zycie">{t('settings.modeZycie')}</SelectItem>
                    <SelectItem value="command">{t('settings.modeCommand')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </Card>

            {/* History Settings */}
            <Card className="hussar-settings-card">
              <div className="hussar-settings-header">
                <h3 className="hussar-settings-section-title">{t('settings.historySection')}</h3>
              </div>
              <div className="hussar-settings-item hussar-settings-switch">
                <div>
                  <Label htmlFor="history" className="hussar-label">{t('settings.historySave')}</Label>
                  <p className="hussar-settings-hint">{t('settings.historyHint')}</p>
                </div>
                <Switch 
                  id="history"
                  checked={saveHistory}
                  onCheckedChange={setSaveHistory}
                />
              </div>
            </Card>

            {/* Account Settings */}
            <Card className="hussar-settings-card">
              <div className="hussar-settings-header">
                <h3 className="hussar-settings-section-title">{t('settings.accountSection')}</h3>
              </div>
              <div className="hussar-settings-item">
                <div className="hussar-account-info">
                  <p className="hussar-account-label">{t('settings.accountStatus')}</p>
                  <p className="hussar-account-value">{t('settings.accountFree')}</p>
                </div>
              </div>
              <Button className="hussar-btn-secondary hussar-btn-full">
                {t('settings.manageAccount')}
              </Button>
            </Card>

            {/* Premium Settings */}
            <Card className="hussar-settings-card hussar-settings-premium">
              <div className="hussar-settings-header">
                <div className="hussar-premium-icon">
                  <Crown className="w-6 h-6" />
                </div>
                <h3 className="hussar-settings-section-title">{t('settings.premiumSection')}</h3>
                <p className="hussar-settings-description">
                  {t('settings.premiumDescription')}
                </p>
              </div>
              <Button 
                className="hussar-btn-primary hussar-btn-full"
                onClick={() => navigate('/pricing')}
              >
                {t('settings.goPremium')}
              </Button>
            </Card>
          </div>

          <div className="hussar-settings-footer">
            <Button 
              className="hussar-btn-secondary"
              onClick={() => navigate('/app')}
            >
              {t('settings.save')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
